import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

// 태블릿 설정용 로그인.
// 아이디 = 매장 고유번호(store_uid), 비밀번호 = 관리자 페이지(/login)와 동일.
// 회원가입은 없다. 로그인 성공 시 토큰을 저장하지 않고, 그 자리에서 테이블 목록을 보여준 뒤
// 선택한 테이블의 고정 주문 주소(/tableorder/<코드>/<테이블코드>)로 이동한다.
const TableOrderLogin = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loginError, setLoginError] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [store, setStore] = useState(null);
  const [tables, setTables] = useState([]);

  const handleChange = (e) => {
    setCode(e.target.value);
    setLoginError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!code.trim()) {
      setLoginError('매장 고유번호를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setLoginError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/tableorder/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() })
      });
      const data = await response.json();

      if (data.success) {
        setStore(data.store);
        setTables(data.tables || []);
      } else {
        setLoginError(data.message || '매장 고유번호가 올바르지 않습니다. 다시 입력해주세요.');
      }
    } catch (error) {
      console.error('테이블오더 로그인 오류:', error);
      setLoginError('서버에 연결할 수 없습니다. 네트워크를 확인해주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  // 키오스크 앱(태블릿) 안에서 열렸는지. 앱이 WowOrderKiosk 브리지를 주입한다.
  const isKioskApp = typeof window !== 'undefined' &&
    !!(window.WowOrderKiosk && window.WowOrderKiosk.setHomeUrl);

  const openTable = (tableCode) => {
    const path = `/tableorder/${store.store_uid}/${encodeURIComponent(tableCode)}`;
    // 앱 안이면 이 주소를 시작 주소로 고정한다 → 재부팅해도 이 테이블 화면이 뜬다.
    // (앱이 아니면 브리지가 없으므로 그냥 이동만 한다)
    try {
      if (isKioskApp) {
        window.WowOrderKiosk.setHomeUrl(window.location.origin + path);
      }
    } catch (error) {
      console.error('시작 주소 고정 실패:', error);
    }
    navigate(path);
  };

  const backToLogin = () => {
    setStore(null);
    setTables([]);
    setCode('');
  };

  // 화면 크기에 따른 스케일 조정
  useEffect(() => {
    const wrapper = document.querySelector('.login-wrapper');
    if (wrapper) {
      wrapper.style.zoom = Math.min(window.innerWidth / 400, 1.5);
    }
  }, [store]);

  // 그룹(층/구역)이 있으면 묶어서 표시
  const groups = tables.reduce((acc, t) => {
    const key = t.group_name || '';
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});
  const groupKeys = Object.keys(groups);

  return (
    <>
      <style jsx="true">{`
        .login-page {
          min-height: 100vh;
          background-color: #FFFFFF;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding-top: 40px;
          -webkit-text-size-adjust: 100%;
          text-size-adjust: 100%;
        }

        .login-wrapper {
          width: 400px;
          transform-origin: top center;
        }

        @media screen and (min-width: 401px) {
          .login-wrapper {
            zoom: calc(100vw / 400);
          }
        }

        @media screen and (min-width: 600px) {
          .login-wrapper {
            zoom: 1.5;
          }
        }

        @media screen and (max-width: 400px) {
          .login-wrapper {
            width: 400px;
            zoom: calc(100vw / 400);
          }
        }

        .login-input::placeholder {
          color: transparent;
        }
        .login-input {
          caret-color: #FDCD27;
        }
        .login-input:focus {
          outline: none;
        }
        .floating-label {
          position: absolute;
          left: 17px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 15px;
          font-weight: 500;
          color: #9D9D9D;
          pointer-events: none;
          transition: all 0.2s ease;
          transform-origin: left center;
          z-index: 2;
        }
        .floating-label.active {
          top: 12px;
          transform: translateY(0);
          font-size: 12px;
        }
        .login-input:-webkit-autofill,
        .login-input:-webkit-autofill:hover,
        .login-input:-webkit-autofill:focus,
        .login-input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px #FFFFFF inset !important;
          box-shadow: 0 0 0 1000px #FFFFFF inset !important;
          -webkit-text-fill-color: #000000 !important;
          font-size: 15px !important;
          font-weight: 500 !important;
          font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif !important;
        }
        .login-page button:focus {
          outline: none;
        }
        .login-page label {
          -webkit-tap-highlight-color: transparent;
          outline: none;
        }
        .table-card {
          border: 1px solid #D9D9D9;
          border-radius: 8px;
          background-color: #FFFFFF;
          padding: 16px 12px;
          cursor: pointer;
          transition: border-color 0.15s, background-color 0.15s;
          text-align: center;
        }
        .table-card:active {
          border-color: #FDCD27;
          background-color: #FFFBEA;
        }
      `}</style>
      <div className="login-page">
        <div className="login-wrapper">
          <div style={styles.card}>
            <div style={styles.logoWrapper}>
              <img src={logo} alt="로고" style={styles.logo} />
            </div>

            {!store ? (
              <>
                <form onSubmit={handleSubmit} style={styles.form}>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      name="code"
                      className="login-input"
                      value={code}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('code')}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        ...styles.input,
                        borderColor: focusedField === 'code' ? '#FDCD27' : '#D9D9D9',
                        borderWidth: focusedField === 'code' ? '1.5px' : '1px',
                        paddingTop: (focusedField === 'code' || code) ? '18px' : '12px',
                        paddingBottom: (focusedField === 'code' || code) ? '2px' : '12px'
                      }}
                      placeholder="매장 고유번호"
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck="false"
                      autoFocus
                    />
                    <span className={`floating-label${focusedField === 'code' || code ? ' active' : ''}`}>
                      매장 고유번호
                    </span>
                  </div>

                  <div style={styles.buttonWrapper}>
                    {loginError && <div style={styles.error}>{loginError}</div>}
                    <button
                      type="submit"
                      style={{
                        ...styles.submitButton,
                        opacity: isLoading ? 0.7 : 1,
                        cursor: isLoading ? 'not-allowed' : 'pointer'
                      }}
                      disabled={isLoading}
                    >
                      {isLoading ? '로그인 중...' : '로그인'}
                    </button>
                  </div>
                </form>

                <div style={styles.helpText}>
                  매장 고유번호는 와우오더 담당자에게 문의해주세요.
                </div>
              </>
            ) : (
              <>
                <div style={styles.storeName}>{store.store_name}</div>
                <div style={styles.guide}>
                  이 태블릿을 놓을 테이블을 선택해주세요.
                  {isKioskApp && <><br />선택하면 이 태블릿에 고정됩니다.</>}
                </div>

                {tables.length === 0 ? (
                  <div style={styles.empty}>
                    등록된 테이블이 없습니다.<br />
                    POS 연동으로 테이블을 먼저 가져와주세요.
                  </div>
                ) : (
                  groupKeys.map(key => (
                    <div key={key || 'default'} style={styles.groupBlock}>
                      {key && <div style={styles.groupTitle}>{key}</div>}
                      <div style={styles.tableGrid}>
                        {groups[key].map(t => (
                          <div
                            key={t.table_code}
                            className="table-card"
                            onClick={() => openTable(t.table_code)}
                          >
                            <div style={styles.tableName}>{t.table_name || t.table_code}</div>
                            <div style={styles.tableCode}>{t.table_code}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}

                <button type="button" onClick={backToLogin} style={styles.secondaryButton}>
                  다른 매장으로 로그인
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const styles = {
  card: {
    backgroundColor: '#FFFFFF',
    padding: '0 20px 40px',
    width: '100%',
    boxSizing: 'border-box'
  },
  logoWrapper: {
    textAlign: 'center',
    marginBottom: '20px'
  },
  logo: {
    width: '91px',
    height: '91px',
    objectFit: 'contain'
  },
  form: {
    display: 'flex',
    flexDirection: 'column'
  },
  input: {
    width: '100%',
    height: '60px',
    padding: '12px 16px',
    border: '1px solid #D9D9D9',
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    fontSize: '15px',
    fontWeight: '500',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s, padding 0.2s'
  },
  buttonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '28px'
  },
  error: {
    color: '#F41717',
    fontSize: '14px',
    fontWeight: '600',
    textAlign: 'left',
    marginBottom: '18px'
  },
  submitButton: {
    width: '100%',
    height: '50px',
    backgroundColor: '#FDCD27',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: '900',
    lineHeight: '18px',
    cursor: 'pointer'
  },
  secondaryButton: {
    width: '100%',
    height: '50px',
    marginTop: '24px',
    backgroundColor: '#FFFFFF',
    color: 'rgb(167, 167, 167)',
    border: '1px solid #D9D9D9',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  helpText: {
    marginTop: '20px',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: '500',
    color: 'rgb(167, 167, 167)'
  },
  storeName: {
    textAlign: 'center',
    fontSize: '20px',
    fontWeight: '900',
    color: '#000000',
    marginBottom: '6px'
  },
  guide: {
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: '500',
    color: '#9D9D9D',
    marginBottom: '24px'
  },
  groupBlock: {
    marginBottom: '20px'
  },
  groupTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#9D9D9D',
    marginBottom: '10px'
  },
  tableGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px'
  },
  tableName: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#000000',
    wordBreak: 'break-all'
  },
  tableCode: {
    marginTop: '4px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#9D9D9D',
    wordBreak: 'break-all'
  },
  empty: {
    textAlign: 'center',
    fontSize: '15px',
    fontWeight: '500',
    color: '#9D9D9D',
    lineHeight: 1.6,
    padding: '30px 0'
  }
};

export default TableOrderLogin;
